import { clamp } from "../utils";
import { SmoothScroll } from "./SmoothScroll";

export class Carousels {
  carouselElements: NodeListOf<HTMLElement> =
    document.querySelectorAll("[data-carousel]");
  // Scope to page modals: other templates (e.g. course pages) use [data-modal="…"] on
  // trigger buttons, which have no backdrop and would throw here.
  modals: NodeListOf<HTMLElement> = document.querySelectorAll(
    ".page-modal[data-modal]",
  );
  smoothScroll: SmoothScroll;

  constructor(smoothScroll: SmoothScroll) {
    this.smoothScroll = smoothScroll;

    this.init();
  }

  init = () => {
    // enable drag when not in mobile
    // force scroll on focus - see button.addEventListener("focus", () => {});

    this.initPageCarousels();
    this.setCardDescriptionTransformProperties();
    this.initModals();
    this.initModalsControls();
  };

  initPageCarousels = () => {
    this.carouselElements.forEach((carousel) => {
      const carouselName = carousel.dataset.carouselFor || "";

      const modal = document.querySelector(
        `.page-modal[data-modal-for="${carouselName}"]`,
      ) as HTMLElement | null;

      const { resetCards, resetObservers } = this.initCarousel(carousel);

      let modalResetCards: (() => void) | null = null;
      let modalResetObservers: (() => void) | null = null;
      let modalMobileResetIndicators: (() => void) | null = null;

      if (modal) {
        const modalCarousel = modal.querySelector(
          "[data-modal-carousel]",
        ) as HTMLElement | null;

        if (modalCarousel) {
          const initModalCarousel = this.initCarousel(modalCarousel);

          modalResetCards = initModalCarousel.resetCards;
          modalResetObservers = initModalCarousel.resetObservers;
        }

        const initMobileIndicators =
          this.initModalCarouselMobileIndicators(modal);
        modalMobileResetIndicators = initMobileIndicators.resetIndicators;
      }

      const scrollContainer = carousel.querySelector(
        ".carousel-inner",
      ) as HTMLElement;

      const pageCarouselFilter = document.querySelector(
        `[data-carousel-filters-for="${carouselName}"]`,
      ) as HTMLElement | null;

      if (pageCarouselFilter) {
        const carouselCards = carousel.querySelectorAll(
          ".carousel-card",
        ) as NodeListOf<HTMLElement>;

        const filterButtons = pageCarouselFilter.querySelectorAll(
          "[data-carousel-filter-tag]",
        ) as NodeListOf<HTMLElement>;

        filterButtons.forEach((button) => {
          button.addEventListener("click", () => {
            const filterTag = button.dataset.carouselFilterTag;

            if (!filterTag) {
              return;
            }

            carouselCards.forEach((card) => {
              const cardTag = card.dataset.carouselCardTag;

              if (!cardTag) {
                return;
              }

              const shouldShowCard = filterTag === "*" || cardTag === filterTag;

              card.classList.toggle("carousel-card--hidden", !shouldShowCard);
            });

            if (modal) {
              const modalCarouselCards = modal.querySelectorAll(
                ".modal-carousel-card",
              ) as NodeListOf<HTMLElement>;

              modalCarouselCards.forEach((card) => {
                const cardTag = card.dataset.carouselCardTag;

                if (!cardTag) {
                  return;
                }

                const shouldShowCard =
                  filterTag === "*" || cardTag === filterTag;

                card.classList.toggle("carousel-card--hidden", !shouldShowCard);
              });
            }

            filterButtons.forEach((btn) => {
              btn.classList.toggle(
                "carousel-filter-button--active",
                btn === button,
              );
            });

            resetCards();

            scrollContainer.style.setProperty("scroll-behavior", "auto");
            scrollContainer.scrollLeft = 0;

            carouselCards.forEach((card) => {
              card.classList.add("carousel-card--filtering");
            });

            requestAnimationFrame(() => {
              scrollContainer.style.removeProperty("scroll-behavior");
              resetObservers();

              setTimeout(() => {
                carouselCards.forEach((card) => {
                  card.classList.remove("carousel-card--filtering");
                });
              }, 200);
            });

            if (modalResetCards && modalResetObservers) {
              modalResetCards();
              modalResetObservers();
            }

            if (modalMobileResetIndicators) {
              modalMobileResetIndicators();
            }
          });
        });
      }
    });
  };

  initCarousel = (carousel: HTMLElement) => {
    const scrollContainer = carousel.querySelector(
      ".carousel-inner",
    ) as HTMLElement;

    let cards = carousel.querySelectorAll(
      ".carousel-card:not(.carousel-card--hidden)",
    ) as NodeListOf<HTMLElement>;
    let firstCard = cards[0];
    let lastCard = [...cards].at(-1)!;

    const prevControl = carousel.querySelector(
      "[data-carousel-prev]",
    ) as HTMLElement;
    const nextControl = carousel.querySelector(
      "[data-carousel-next]",
    ) as HTMLElement;

    let horizontalOffset = parseInt(
      getComputedStyle(scrollContainer).paddingLeft,
    );

    let intersectionObservers: IntersectionObserver[] = [];

    const initObservers = () => {
      const firstCardIntersectionObserver = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          entries.forEach((entry) => {
            prevControl.toggleAttribute("disabled", entry.isIntersecting);
          });
        },
        {
          threshold: 0.1,
          rootMargin: `1000px 0px 1000px -${horizontalOffset}px`,
        },
      );

      firstCardIntersectionObserver.observe(firstCard);

      const lastCardIntersectionObserver = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          entries.forEach((entry) => {
            nextControl.toggleAttribute("disabled", entry.isIntersecting);
          });
        },
        {
          threshold: 0.9,
          rootMargin: `1000px -${horizontalOffset}px 1000px 0px`,
        },
      );

      lastCardIntersectionObserver.observe(lastCard);

      intersectionObservers.push(
        firstCardIntersectionObserver,
        lastCardIntersectionObserver,
      );

      cards.forEach((card) => {
        const cardIntersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              card.classList.toggle(
                "carousel-card--passive",
                !entry.isIntersecting,
              );
            });
          },
          {
            threshold: 0.1,
            rootMargin: `1000px -${horizontalOffset}px 1000px -${horizontalOffset}px`,
          },
        );

        cardIntersectionObserver.observe(card);
        intersectionObservers.push(cardIntersectionObserver);
      });
    };

    initObservers();

    const resetObservers = () => {
      intersectionObservers.forEach((observer) => observer.disconnect());
      intersectionObservers = [];

      initObservers();
    };

    const resetCards = () => {
      cards = carousel.querySelectorAll(
        ".carousel-card:not(.carousel-card--hidden)",
      ) as NodeListOf<HTMLElement>;
      firstCard = cards[0];
      lastCard = [...cards].at(-1)!;
    };

    window.addEventListener("resize", () => {
      horizontalOffset = parseInt(
        getComputedStyle(scrollContainer).paddingLeft,
      );

      resetObservers();
    });

    prevControl.addEventListener("click", () => {
      const cardWidth = firstCard.offsetWidth;
      scrollContainer.scrollLeft -= cardWidth;
    });

    nextControl.addEventListener("click", () => {
      const cardWidth = firstCard.offsetWidth;
      scrollContainer.scrollLeft += cardWidth;
    });

    return {
      resetCards,
      resetObservers,
    };
  };

  initModalCarouselMobileIndicators = (modal: HTMLElement) => {
    const modalMobileCarouselIndicators = modal.querySelector(
      "[data-modal-carousel-mobile-indicators]",
    ) as HTMLElement | null;

    if (!modalMobileCarouselIndicators) {
      return {
        resetIndicators: () => {},
      };
    }

    const indicatorsTrack = document.createElement("div");
    indicatorsTrack.className = "modal-carousel-mobile-indicators-track";
    modalMobileCarouselIndicators.appendChild(indicatorsTrack);

    let indicators: HTMLElement[] = [];

    const setIndicators = () => {
      indicators = [];
      indicatorsTrack.innerHTML = "";

      const totalVisibleCards = modal.querySelectorAll(
        ".carousel-card:not(.carousel-card--hidden)",
      ).length;

      if (totalVisibleCards <= 1) {
        return;
      }

      for (let i = 0; i < totalVisibleCards; i++) {
        const indicator = document.createElement("span");
        indicator.className = "modal-carousel-mobile-indicator";
        indicatorsTrack.appendChild(indicator);
        indicators.push(indicator);
      }
    };

    setIndicators();

    const scrollContainer = modal.querySelector(
      ".carousel-inner",
    ) as HTMLElement;

    const updateIndicators = () => {
      if (indicators.length === 0) {
        return;
      }

      const cardWidth = scrollContainer.querySelector(
        ".modal-carousel-card:not(.carousel-card--hidden)",
      )?.clientWidth;

      if (!cardWidth) {
        return;
      }

      const activeIndex = Math.round(scrollContainer.scrollLeft / cardWidth);
      const visibleIndicatorsCount = 4;
      const trackGap =
        parseInt(getComputedStyle(indicatorsTrack).columnGap) || 0;
      const indicatorStride = indicators[0].offsetWidth + trackGap;
      const maxStartIndex = Math.max(
        0,
        indicators.length - visibleIndicatorsCount,
      );
      const startIndex = clamp(activeIndex - 1, 0, maxStartIndex);
      const centeredOverflowOffset = Math.max(
        0,
        (indicatorsTrack.scrollWidth -
          modalMobileCarouselIndicators.clientWidth) *
          0.5,
      );

      indicators.forEach((indicator, index) => {
        const distanceFromActive = Math.abs(activeIndex - index);
        const indicatorOpacity = Math.max(0.2, 1 - distanceFromActive * 0.2);

        indicator.classList.toggle(
          "modal-carousel-mobile-indicator--active",
          index === activeIndex,
        );
        indicator.style.setProperty(
          "--indicator-opacity",
          indicatorOpacity.toFixed(2),
        );
      });

      modalMobileCarouselIndicators.style.setProperty(
        "--indicators-translate-x",
        `${centeredOverflowOffset - startIndex * indicatorStride}px`,
      );
    };

    updateIndicators();

    const resetIndicators = () => {
      setIndicators();
      updateIndicators();
    };

    scrollContainer.addEventListener("scroll", updateIndicators);
    window.addEventListener("resize", updateIndicators);

    return {
      resetIndicators,
    };
  };

  setCardDescriptionTransformProperties = () => {
    const carouselCardDescription = document.querySelectorAll(
      ".carousel-card-description",
    ) as NodeListOf<HTMLElement>;

    carouselCardDescription.forEach((description) => {
      const shortDescription = description.querySelector(
        ".carousel-card-description-short",
      ) as HTMLElement;
      const fullDescription = description.querySelector(
        ".carousel-card-description-full",
      ) as HTMLElement;

      const setOffsetY = () => {
        const shortDescriptionHeight = shortDescription.offsetHeight;
        const fullDescriptionHeight = fullDescription.offsetHeight;

        const descriptionDifferenceInHeight =
          fullDescriptionHeight - shortDescriptionHeight;

        description.style.setProperty(
          "--description-offset-y",
          `-${descriptionDifferenceInHeight}px`,
        );
      };

      setOffsetY();

      const resizeObserver = new ResizeObserver(setOffsetY);

      resizeObserver.observe(shortDescription);
      resizeObserver.observe(fullDescription);
    });
  };

  initModals = () => {
    this.modals.forEach((modal) => {
      const modalBackdrop = modal.querySelector(
        "[data-modal-backdrop]",
      ) as HTMLElement;

      modalBackdrop.addEventListener("click", () => this.closeModal(modal));

      const closeModalButtons = modal.querySelectorAll(
        "[data-modal-close]",
      ) as NodeListOf<HTMLElement>;

      closeModalButtons.forEach((button) => {
        button.addEventListener("click", () => {
          button.blur();
          this.closeModal(modal);
        });
      });
    });

    window.addEventListener("keydown", this.onWindowKeyDown);
  };

  initModalsControls = () => {
    this.carouselElements.forEach((carousel) => {
      const modalFor = carousel.dataset.carouselFor;

      if (!modalFor) {
        return;
      }

      const modal = document.querySelector(
        `.page-modal[data-modal-for="${modalFor}"]`,
      ) as HTMLElement | null;

      if (!modal) {
        return;
      }

      const modalCarouselInner = modal.querySelector(
        ".carousel-inner",
      ) as HTMLElement;

      const openModalButtons = carousel.querySelectorAll(
        ".carousel-card-button",
      ) as NodeListOf<HTMLElement>;

      const signUpButtons = carousel.querySelectorAll(
        "[data-carousel-sign-up]",
      ) as NodeListOf<HTMLElement>;

      const carouselCards = carousel.querySelectorAll(".carousel-card");

      const openModal = (button: HTMLElement) => {
        modal.setAttribute("aria-hidden", "false");
        modal.classList.add("page-modal--open");
        this.smoothScroll.toggleLenisDocumentBodyScroll(false);

        if (modalCarouselInner) {
          const activeCarouselCards = [...carouselCards].filter(
            (card) => !card.classList.contains("carousel-card--hidden"),
          );

          const buttonCard = button.closest(".carousel-card") as HTMLElement;

          const cardIndex = activeCarouselCards.indexOf(buttonCard);

          const carouselCardWidth =
            modalCarouselInner.querySelector(
              ".modal-carousel-card:not(.carousel-card--hidden)",
            )?.clientWidth ?? 0;

          modalCarouselInner.style.setProperty("scroll-behavior", "auto");
          modalCarouselInner.scrollLeft = cardIndex * carouselCardWidth;

          requestAnimationFrame(() => {
            modalCarouselInner.style.removeProperty("scroll-behavior");
          });
        }
      };

      openModalButtons.forEach((button) => {
        button.addEventListener("click", () => {
          openModal(button);
        });

        button.addEventListener("focus", () => {});
      });

      signUpButtons.forEach((button) => {
        button.addEventListener("click", () => {
          openModal(button);
        });

        button.addEventListener("focus", () => {});
      });
    });
  };

  closeModal = (modal: HTMLElement) => {
    modal.setAttribute("aria-hidden", "true");
    modal.classList.remove("page-modal--open");
    this.smoothScroll.toggleLenisDocumentBodyScroll(true);

    const modalCardContents = modal.querySelectorAll(
      ".modal-carousel-card-content",
    ) as NodeListOf<HTMLElement>;

    modalCardContents.forEach((content) => {
      content.scrollTop = 0;
    });

    const modalCardDetails = modal.querySelectorAll(
      ".modal-carousel-card-details",
    ) as NodeListOf<HTMLElement>;

    modalCardDetails.forEach((details) => {
      details.scrollTop = 0;
    });

    const modalCardPhoneProductScreens = modal.querySelectorAll(
      ".modal-carousel-card-phone-product-screens",
    ) as NodeListOf<HTMLElement>;

    modalCardPhoneProductScreens.forEach((screens) => {
      screens.scrollTop = 0;
    });

    const modalCarouselInner = modal.querySelector(
      ".carousel-inner",
    ) as HTMLElement;

    modalCarouselInner.style.setProperty("scroll-behavior", "auto");

    requestAnimationFrame(() => {
      modalCarouselInner.scrollLeft = 0;
      modalCarouselInner.style.removeProperty("scroll-behavior");
    });
  };

  onWindowKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      const activeModal = [...this.modals].find((modal) =>
        modal.classList.contains("page-modal--open"),
      );

      if (activeModal) {
        this.closeModal(activeModal);
      }
    }
  };
}
